import { useEffect, useState } from "react";
import { api, formatError } from "@/lib/api";
import Header from "@/components/Header";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Users, UserCheck, MessageSquareText, ShieldCheck, Activity, RefreshCw } from "lucide-react";
import AdminBets from "@/components/AdminBets";
import TeamPanel from "@/components/TeamPanel";

const Stat = ({ label, value, icon: Icon, testid }) => (
  <Card className="bg-zinc-950 border-amber-500/20" data-testid={testid}>
    <CardContent className="p-5 flex items-center justify-between">
      <div>
        <div className="text-[11px] uppercase font-display tracking-widest text-amber-400">{label}</div>
        <div className="mt-1 font-mono-stat text-3xl text-zinc-100">{value}</div>
      </div>
      {Icon && <Icon className="h-7 w-7 text-amber-400/60" />}
    </CardContent>
  </Card>
);

export default function AdminDashboard() {
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [ledger, setLedger] = useState([]);
  const [settings, setSettings] = useState({ whatsapp_number: "", whatsapp_message: "" });
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    const [s, l, g, se] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/audit-logs"), api.get("/admin/credits-ledger"), api.get("/admin/settings"),
    ]);
    setStats(s.data); setLogs(l.data.items); setLedger(g.data.items); setSettings(se.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="min-h-screen bg-[#09090D] text-zinc-100">
      <Header />
      <div className="mx-auto max-w-7xl px-4 sm:px-8 py-10">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <div className="text-xs uppercase font-display tracking-widest text-amber-400">Master Console</div>
            <h1 className="font-display text-4xl font-black uppercase gold-text mt-1">Admin Dashboard</h1>
          </div>
          <div className="flex gap-2 flex-wrap items-center">
            <Button
              size="sm"
              disabled={refreshing || !stats?.odds_api_configured}
              data-testid="refresh-odds-btn"
              onClick={async () => {
                setRefreshing(true);
                try {
                  const { data } = await api.post("/admin/odds/refresh");
                  toast.success(`Odds refreshed · ${data.events} events · ${data.credits_remaining} credits left`);
                  load();
                } catch (e) { toast.error(formatError(e)); }
                finally { setRefreshing(false); }
              }}
              className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-600 text-black font-display uppercase tracking-widest"
            >
              <RefreshCw className={`h-3 w-3 mr-1 ${refreshing ? "animate-spin" : ""}`} /> Refresh Odds Now
            </Button>
            <Badge className={stats?.odds_api_configured ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40" : "bg-amber-500/15 text-amber-300 border border-amber-500/40"} data-testid="odds-api-badge">
              Odds API: {stats?.odds_api_configured ? `Live · ${stats?.odds_credits_remaining ?? "?"} credits left · auto every ${Math.round((stats?.odds_refresh_every_seconds || 0) / 60)}m` : "Off"}
            </Badge>
            <Badge className={stats?.cricket_api_configured ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/40" : "bg-amber-500/15 text-amber-300 border border-amber-500/40"}>
              <ShieldCheck className="h-3 w-3 mr-1" />
              Live Scores: {stats?.cricket_api_configured ? "On" : "Not connected"}
            </Badge>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <Stat label="Masters" value={stats?.masters_total ?? "—"} icon={UserCheck} testid="stat-masters" />
          <Stat label="Agents" value={stats?.agents_total ?? "—"} icon={UserCheck} testid="stat-agents" />
          <Stat label="Clients" value={stats?.clients_total ?? "—"} icon={Users} testid="stat-total" />
          <Stat label="Last Sync" value={stats?.odds_last_sync ? new Date(stats.odds_last_sync).toLocaleTimeString() : "—"} icon={Activity} testid="stat-sync" />
        </div>

        <Tabs defaultValue="clients">
          <TabsList className="bg-zinc-950 border border-amber-500/20">
            <TabsTrigger value="clients" data-testid="tab-admin-clients" className="font-display uppercase tracking-widest data-[state=active]:bg-amber-400 data-[state=active]:text-black">Team</TabsTrigger>
            <TabsTrigger value="ledger" data-testid="tab-admin-ledger" className="font-display uppercase tracking-widest data-[state=active]:bg-amber-400 data-[state=active]:text-black">Credit Ledger</TabsTrigger>
            <TabsTrigger value="logs" data-testid="tab-admin-logs" className="font-display uppercase tracking-widest data-[state=active]:bg-amber-400 data-[state=active]:text-black">Audit Logs</TabsTrigger>
            <TabsTrigger value="bets" data-testid="tab-admin-bets" className="font-display uppercase tracking-widest data-[state=active]:bg-amber-400 data-[state=active]:text-black">Bets</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-admin-settings" className="font-display uppercase tracking-widest data-[state=active]:bg-amber-400 data-[state=active]:text-black">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="clients" className="mt-6">
            <TeamPanel onChanged={load} />
          </TabsContent>

          <TabsContent value="ledger" className="mt-6">
            <Card className="bg-zinc-950 border-amber-500/20">
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-amber-500/15">
                      <TableHead className="text-amber-400 font-display uppercase tracking-widest">Time</TableHead>
                      <TableHead className="text-amber-400 font-display uppercase tracking-widest">Client</TableHead>
                      <TableHead className="text-amber-400 font-display uppercase tracking-widest">Delta</TableHead>
                      <TableHead className="text-amber-400 font-display uppercase tracking-widest">Prev</TableHead>
                      <TableHead className="text-amber-400 font-display uppercase tracking-widest">New</TableHead>
                      <TableHead className="text-amber-400 font-display uppercase tracking-widest">Reason</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ledger.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-zinc-500 py-8">No credit activity.</TableCell></TableRow>}
                    {ledger.map((l, i) => (
                      <TableRow key={i} className="border-amber-500/10">
                        <TableCell className="text-xs text-zinc-400">{new Date(l.at).toLocaleString()}</TableCell>
                        <TableCell className="font-mono-stat text-xs">{l.client_id}</TableCell>
                        <TableCell className={`font-mono-stat ${l.delta >= 0 ? "text-emerald-300" : "text-red-300"}`}>{l.delta >= 0 ? "+" : ""}{l.delta.toFixed(2)}</TableCell>
                        <TableCell className="font-mono-stat">{l.previous.toFixed(2)}</TableCell>
                        <TableCell className="font-mono-stat text-amber-300">{l.new_balance.toFixed(2)}</TableCell>
                        <TableCell>{l.reason}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="logs" className="mt-6">
            <Card className="bg-zinc-950 border-amber-500/20">
              <CardContent className="p-0 overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-amber-500/15">
                      <TableHead className="text-amber-400 font-display uppercase tracking-widest">Time</TableHead>
                      <TableHead className="text-amber-400 font-display uppercase tracking-widest">Event</TableHead>
                      <TableHead className="text-amber-400 font-display uppercase tracking-widest">Actor</TableHead>
                      <TableHead className="text-amber-400 font-display uppercase tracking-widest">Target</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {logs.map((l, i) => (
                      <TableRow key={i} className="border-amber-500/10" data-testid={`audit-row-${i}`}>
                        <TableCell className="text-xs text-zinc-400">{new Date(l.at).toLocaleString()}</TableCell>
                        <TableCell className="font-mono-stat text-amber-300">{l.event}</TableCell>
                        <TableCell className="font-mono-stat text-xs">{l.actor || "—"}</TableCell>
                        <TableCell className="font-mono-stat text-xs">{l.target || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="bets" className="mt-6">
            <AdminBets onChanged={load} />
          </TabsContent>

          <TabsContent value="settings" className="mt-6">
            <Card className="bg-zinc-950 border-amber-500/20 max-w-2xl">
              <CardContent className="p-6 space-y-5">
                <div className="flex items-center gap-2 text-amber-400 font-display uppercase tracking-widest text-sm">
                  <MessageSquareText className="h-4 w-4" /> WhatsApp Support
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest font-display text-amber-400">Phone number (with country code)</Label>
                  <Input value={settings.whatsapp_number || ""} onChange={(e) => setSettings({ ...settings, whatsapp_number: e.target.value })} placeholder="+919999999999" className="bg-black border-amber-500/20 mt-1.5" data-testid="whatsapp-input" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest font-display text-amber-400">Default message</Label>
                  <Input value={settings.whatsapp_message || ""} onChange={(e) => setSettings({ ...settings, whatsapp_message: e.target.value })} className="bg-black border-amber-500/20 mt-1.5" data-testid="whatsapp-message" />
                </div>
                <Button
                  onClick={async () => {
                    try { await api.put("/admin/settings", settings); toast.success("Settings saved"); load(); }
                    catch (e) { toast.error(formatError(e)); }
                  }}
                  data-testid="save-settings-btn"
                  className="bg-gradient-to-r from-amber-300 via-amber-400 to-amber-600 text-black font-display uppercase tracking-widest"
                >
                  Save Settings
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
